import React, { useEffect, useState } from 'react';
import { AlertTriangle, Lock, Camera, PhoneOff, FileText, ArrowRight, ArrowLeft, CheckCircle2, Circle, Award } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { StepperUI } from '../components/StepperUI';
import { WarningBanner } from '../components/WarningBanner';
import { SEO } from '../components/SEO';
interface ChecklistItem {
  id: string;
  text: string;
  critical?: boolean;
}
interface StepData {
  id: number;
  title: string;
  icon: any;
  items?: ChecklistItem[];
}
export function EmergencyProtocolPage() {
  const {
    language,
    t
  } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [completionScore, setCompletionScore] = useState(0);
  const steps: StepData[] = [{
    id: 1,
    title: t('emergency.step1.title'),
    icon: AlertTriangle,
    items: [{
      id: 'stop-money',
      text: t('emergency.step1.item1'),
      critical: true
    }, {
      id: 'no-fees',
      text: t('emergency.step1.item2'),
      critical: true
    }, {
      id: 'call-bank',
      text: t('emergency.step1.item3'),
      critical: true
    }]
  }, {
    id: 2,
    title: t('emergency.step2.title'),
    icon: Lock,
    items: [{
      id: 'change-passwords',
      text: t('emergency.step2.item1')
    }, {
      id: 'enable-2fa',
      text: t('emergency.step2.item2')
    }, {
      id: 'logout-devices',
      text: t('emergency.step2.item3')
    }]
  }, {
    id: 3,
    title: t('emergency.step3.title'),
    icon: Camera,
    items: [{
      id: 'screenshots',
      text: t('emergency.step3.item1')
    }, {
      id: 'transaction-ids',
      text: t('emergency.step3.item2')
    }, {
      id: 'save-urls',
      text: t('emergency.step3.item3')
    }, {
      id: 'keep-messages',
      text: t('emergency.step3.item4')
    }]
  }, {
    id: 4,
    title: t('emergency.step4.title'),
    icon: PhoneOff,
    items: [{
      id: 'block-contacts',
      text: t('emergency.step4.item1')
    }, {
      id: 'no-reply',
      text: t('emergency.step4.item2')
    }]
  }, {
    id: 5,
    title: t('emergency.step5.title'),
    icon: FileText
  }, {
    id: 6,
    title: t('emergency.step6.title'),
    icon: CheckCircle2
  }];
  // Calculate total checkable items
  const totalItems = steps.filter(step => step.items).reduce((sum, step) => sum + (step.items?.length || 0), 0);
  // Calculate completion percentage
  useEffect(() => {
    const completedCount = Object.values(checkedItems).filter(Boolean).length;
    const percentage = totalItems > 0 ? Math.round(completedCount / totalItems * 100) : 0;
    setCompletionScore(percentage);
  }, [checkedItems, totalItems]);
  // Get completion for current step
  const getCurrentStepCompletion = () => {
    const currentStepData = steps.find(s => s.id === currentStep);
    if (!currentStepData?.items) return 100;
    const stepItems = currentStepData.items;
    const completedInStep = stepItems.filter(item => checkedItems[item.id]).length;
    return Math.round(completedInStep / stepItems.length * 100);
  };
  const handleCheckItem = (itemId: string) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  const renderChecklistItems = (items: ChecklistItem[]) => {
    return <div className="space-y-3" data-id="element-673">
        {items.map(item => <label key={item.id} className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 touch-manipulation ${checkedItems[item.id] ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-674">
            <input type="checkbox" checked={checkedItems[item.id] || false} onChange={() => handleCheckItem(item.id)} className="sr-only" data-id="element-675" />
            <div className="flex-shrink-0 mt-0.5" data-id="element-676">
              {checkedItems[item.id] ? <CheckCircle2 className="h-6 w-6 text-green-600" data-id="element-677" /> : <Circle className="h-6 w-6 text-slate-300" data-id="element-678" />}
            </div>
            <div className="ml-3 flex-1" data-id="element-679">
              <span className={`text-sm sm:text-base ${checkedItems[item.id] ? 'text-green-900 font-medium' : 'text-slate-700'}`} data-id="element-680">
                {item.text}
              </span>
              {item.critical && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-681">
                  {t('emergency.critical')}
                </span>}
            </div>
          </label>)}
      </div>;
  };
  const renderStepContent = () => {
    const currentStepData = steps.find(s => s.id === currentStep);
    switch (currentStep) {
      case 1:
        return <div className="space-y-6" data-id="element-682">
            <div className="bg-red-50 border border-red-200 rounded-xl p-5" data-id="element-683">
              <h3 className="text-lg font-bold text-red-900 mb-2 flex items-center" data-id="element-684">
                <AlertTriangle className="h-5 w-5 mr-2" data-id="element-685" />
                {t('emergency.immediateAction')}
              </h3>
              <p className="text-sm text-red-800" data-id="element-686">
                {t('emergency.step1.desc')}
              </p>
            </div>
            {currentStepData?.items && renderChecklistItems(currentStepData.items)}
            <p className="text-sm text-slate-600 italic" data-id="element-687">
              {t('emergency.step1.note')}
            </p>
          </div>;
      case 2:
        return <div className="space-y-6" data-id="element-688">
            <div data-id="element-689">
              <h3 className="text-xl font-bold text-primary-900 mb-2" data-id="element-690">
                {t('emergency.step2.title')}
              </h3>
              <p className="text-sm text-slate-600" data-id="element-691">
                {t('emergency.step2.desc')}
              </p>
            </div>
            {currentStepData?.items && renderChecklistItems(currentStepData.items)}
          </div>;
      case 3:
        return <div className="space-y-6" data-id="element-692">
            <div data-id="element-693">
              <h3 className="text-xl font-bold text-primary-900 mb-2" data-id="element-694">
                {t('emergency.step3.title')}
              </h3>
              <WarningBanner message={t('emergency.step3.warning')} dismissible={false} variant="danger" data-id="element-695" />
            </div>
            {currentStepData?.items && renderChecklistItems(currentStepData.items)}
          </div>;
      case 4:
        return <div className="space-y-6" data-id="element-696">
            <div data-id="element-697">
              <h3 className="text-xl font-bold text-primary-900 mb-2" data-id="element-698">
                {t('emergency.step4.title')}
              </h3>
              <p className="text-sm text-slate-600" data-id="element-699">
                {t('emergency.step4.desc')}
              </p>
            </div>
            {currentStepData?.items && renderChecklistItems(currentStepData.items)}
          </div>;
      case 5:
        return <div className="space-y-6" data-id="element-700">
            <div data-id="element-701">
              <h3 className="text-xl font-bold text-primary-900 mb-2" data-id="element-702">
                {t('emergency.step5.title')}
              </h3>
              <p className="text-sm text-slate-600 mb-4" data-id="element-703">
                {t('emergency.step5.desc')}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3" data-id="element-704">
              <Button href={`/${language}/protocols/social-engineering`} variant="outline" className="justify-start text-left h-auto py-4" data-id="element-705">
                <span className="text-sm sm:text-base" data-id="element-706">
                  {t('scam.socialEngineering')}
                </span>
              </Button>
              <Button href={`/${language}/protocols/investment-crypto`} variant="outline" className="justify-start text-left h-auto py-4" data-id="element-707">
                <span className="text-sm sm:text-base" data-id="element-708">
                  {t('scam.investmentFraud')}
                </span>
              </Button>
              <Button href={`/${language}/protocols/job-scam`} variant="outline" className="justify-start text-left h-auto py-4" data-id="element-709">
                <span className="text-sm sm:text-base" data-id="element-710">
                  {t('scam.jobScam')}
                </span>
              </Button>
              <Button href={`/${language}/where-to-report`} variant="accent" className="justify-center mt-2" data-id="element-711">
                {t('protocol.findReportingChannels')}
              </Button>
            </div>
          </div>;
      case 6:
        return <div className="space-y-6" data-id="element-712">
            {/* Completion Score */}
            <div className="text-center py-8" data-id="element-713">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent-100 mb-4" data-id="element-714">
                <Award className="h-10 w-10 text-accent-600" data-id="element-715" />
              </div>
              <h3 className="text-2xl font-bold text-primary-900 mb-2" data-id="element-716">
                {t('emergency.protocolCompletion')}
              </h3>
              <div className="text-5xl font-bold text-accent-600 mb-2" data-id="element-717">
                {completionScore}%
              </div>
              <p className="text-slate-600" data-id="element-718">
                {Object.values(checkedItems).filter(Boolean).length} of{' '}
                {totalItems} {t('emergency.stepsCompleted')}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="relative w-full h-4 bg-slate-200 rounded-full overflow-hidden" data-id="element-719">
              <div className="h-full bg-gradient-to-r from-accent-500 to-accent-600 transition-all duration-700 ease-out" style={{
              width: `${completionScore}%`
            }} data-id="element-720" />
            </div>

            {/* Recommendations based on score */}
            <div className={`rounded-xl p-6 ${completionScore >= 80 ? 'bg-green-50 border border-green-200' : completionScore >= 50 ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'}`} data-id="element-721">
              <h4 className="font-bold text-lg mb-3" data-id="element-722">
                {completionScore >= 80 && `✓ ${t('emergency.excellentProgress')}`}
                {completionScore >= 50 && completionScore < 80 && `⚠ ${t('emergency.goodStart')}`}
                {completionScore < 50 && `⚠ ${t('emergency.actionRequired')}`}
              </h4>
              <p className="text-sm mb-4" data-id="element-723">
                {completionScore >= 80 && t('emergency.completion.high')}
                {completionScore >= 50 && completionScore < 80 && t('emergency.completion.medium')}
                {completionScore < 50 && t('emergency.completion.low')}
              </p>

              {/* Incomplete items reminder */}
              {completionScore < 100 && <div className="mt-4 pt-4 border-t border-current/20" data-id="element-724">
                  <p className="text-sm font-medium mb-2" data-id="element-725">
                    {t('emergency.incompleteSteps')}
                  </p>
                  <ul className="text-sm space-y-1" data-id="element-726">
                    {steps.filter(step => step.items).map(step => {
                  const incomplete = step.items?.filter(item => !checkedItems[item.id]) || [];
                  if (incomplete.length > 0) {
                    return <li key={step.id} className="ml-4" data-id="element-727">
                              • {step.title}: {incomplete.length}{' '}
                              {t('emergency.itemsRemaining')}
                            </li>;
                  }
                  return null;
                })}
                  </ul>
                </div>}
            </div>

            {/* Next Actions */}
            <div className="space-y-3" data-id="element-728">
              <Button href={`/${language}/where-to-report`} variant="accent" size="lg" fullWidth data-id="element-729">
                {t('emergency.reportToAuthorities')}
              </Button>
              <Button onClick={() => setCurrentStep(1)} variant="outline" fullWidth data-id="element-730">
                {t('emergency.reviewProtocol')}
              </Button>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 sm:pb-16 max-w-4xl" data-id="element-731">
      <SEO title={t('seo.emergency.title')} description={t('seo.emergency.desc')} data-id="element-732" />
      <div className="text-center mb-6 sm:mb-8" data-id="element-733">
        <div className="inline-flex items-center justify-center p-2 sm:p-3 bg-red-100 rounded-full mb-3 sm:mb-4" data-id="element-734">
          <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-destructive" data-id="element-735" />
        </div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary-900 mb-2" data-id="element-736">
          {t('emergency.title')}
        </h1>
        <p className="text-sm sm:text-base text-slate-600 px-4" data-id="element-737">
          {t('emergency.subtitle')}
        </p>
      </div>

      {/* Overall Progress Indicator */}
      <div className="mb-6 bg-white rounded-xl p-4 shadow-soft border border-slate-200" data-id="element-738">
        <div className="flex items-center justify-between mb-2" data-id="element-739">
          <span className="text-sm font-medium text-slate-700" data-id="element-740">
            {t('emergency.overallProgress')}
          </span>
          <span className="text-sm font-bold text-accent-600" data-id="element-741">
            {completionScore}%
          </span>
        </div>
        <div className="relative w-full h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-742">
          <div className="h-full bg-accent-600 transition-all duration-500 ease-out" style={{
          width: `${completionScore}%`
        }} data-id="element-743" />
        </div>
        <p className="text-xs text-slate-500 mt-2" data-id="element-744">
          {Object.values(checkedItems).filter(Boolean).length} of {totalItems}{' '}
          {t('emergency.actionsCompleted')}
        </p>
      </div>

      <div className="mb-6 sm:mb-8" data-id="element-745">
        <StepperUI steps={steps} currentStep={currentStep} data-id="element-746" />
      </div>

      {/* Step Completion Badge */}
      {currentStep <= 4 && <div className="mb-4 flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-200" data-id="element-747">
          <span className="text-sm font-medium text-slate-700" data-id="element-748">
            {t('emergency.stepProgress')}
          </span>
          <span className={`text-sm font-bold ${getCurrentStepCompletion() === 100 ? 'text-green-600' : 'text-accent-600'}`} data-id="element-749">
            {getCurrentStepCompletion()}%
            {getCurrentStepCompletion() === 100 && ' ✓'}
          </span>
        </div>}

      <Card className="p-4 sm:p-6 md:p-8" data-id="element-750">
        <div className="min-h-[300px] flex flex-col" data-id="element-751">
          <div className="flex-grow" data-id="element-752">{renderStepContent()}</div>

          <div className="flex flex-col sm:flex-row justify-between gap-3 mt-8 pt-6 border-t border-slate-200" data-id="element-753">
            <Button variant="outline" onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1} className="w-full sm:w-auto order-2 sm:order-1" leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-755" />} data-id="element-754">
              {t('action.previous')}
            </Button>

            {currentStep < 6 ? <Button variant="accent" onClick={() => setCurrentStep(Math.min(6, currentStep + 1))} className="w-full sm:w-auto order-1 sm:order-2" rightIcon={<ArrowRight className="h-4 w-4" data-id="element-757" />} data-id="element-756">
                {currentStep === 5 ? t('emergency.viewCompletion') : t('action.next')}
              </Button> : <div className="hidden sm:block" data-id="element-758" />}
          </div>
        </div>
      </Card>
    </div>;
}